/*
 * digital-limit.js
 * Limits digital (non-shippable) products to a maximum quantity per order.
 *
 * How it works:
 *  - Liquid renders `data-digital-limit="1"` on the quantity <input> and the
 *    plus <button> of every cart line whose item.requires_shipping == false
 *    (cart page + cart drawer).
 *  - This script uses capture-phase event delegation so it keeps working after
 *    the theme re-renders the cart sections via AJAX.
 *  - If a customer tries to raise a digital line above the limit, it shows a
 *    warning dialog and snaps the value back to the limit.
 *  - On load / after every cart re-render it also auto-corrects any digital
 *    line that is already above the limit (e.g. added to cart twice).
 *
 * The default limit is 1 and is set per-element via the data attribute, so it
 * can be changed in Liquid without touching this file.
 */
(function () {
  'use strict';

  var DEFAULT_MAX = 1;
  var MESSAGE =
    window.digitalLimitMessage || 'Digital products are limited to 1 per order.';
  var BUTTON_LABEL = window.digitalLimitButtonLabel || 'OK';

  /* ----------------------------- warning dialog ----------------------------- */

  var modal = null;
  var lastShown = 0;

  function buildModal() {
    var style = document.createElement('style');
    style.textContent =
      '.digital-limit-modal{position:fixed;inset:0;z-index:2147483000;display:none;align-items:center;justify-content:center;}' +
      '.digital-limit-modal.is-open{display:flex;}' +
      '.digital-limit-modal__overlay{position:absolute;inset:0;background:rgba(0,0,0,.5);}' +
      '.digital-limit-modal__box{position:relative;max-width:90vw;width:360px;background:#fff;color:#111;border-radius:10px;padding:24px 22px 18px;box-shadow:0 10px 40px rgba(0,0,0,.25);text-align:center;font-family:inherit;}' +
      '.digital-limit-modal__text{margin:0 0 18px;font-size:15px;line-height:1.5;}' +
      '.digital-limit-modal__btn{appearance:none;border:0;cursor:pointer;background:#111;color:#fff;border-radius:6px;padding:10px 26px;font-size:14px;font-weight:600;}' +
      '.digital-limit-modal__btn:hover{opacity:.85;}';
    document.head.appendChild(style);

    modal = document.createElement('div');
    modal.className = 'digital-limit-modal';
    modal.setAttribute('role', 'alertdialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('aria-hidden', 'true');
    modal.innerHTML =
      '<div class="digital-limit-modal__overlay" data-digital-close></div>' +
      '<div class="digital-limit-modal__box">' +
      '<p class="digital-limit-modal__text"></p>' +
      '<button type="button" class="digital-limit-modal__btn" data-digital-close></button>' +
      '</div>';
    document.body.appendChild(modal);

    modal.querySelector('.digital-limit-modal__btn').textContent = BUTTON_LABEL;
    modal.addEventListener('click', function (e) {
      if (e.target && e.target.hasAttribute('data-digital-close')) hide();
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') hide();
    });
  }

  function hide() {
    if (!modal) return;
    modal.classList.remove('is-open');
    modal.setAttribute('aria-hidden', 'true');
  }

  function warn() {
    // Debounce so a single action never stacks multiple dialogs.
    var now = Date.now();
    if (now - lastShown < 500) return;
    lastShown = now;

    if (!modal) buildModal();
    modal.querySelector('.digital-limit-modal__text').textContent = MESSAGE;
    modal.classList.add('is-open');
    modal.setAttribute('aria-hidden', 'false');
    var btn = modal.querySelector('.digital-limit-modal__btn');
    if (btn) btn.focus();
  }

  /* ------------------------------- helpers ---------------------------------- */

  function getMax(el) {
    var v = parseInt(el.getAttribute('data-digital-limit'), 10);
    return isNaN(v) || v < 1 ? DEFAULT_MAX : v;
  }

  function inputForButton(btn) {
    var wrap = btn.parentNode;
    return wrap ? wrap.querySelector('input[id^="cart-qty-"]') : null;
  }

  /* -------------------------- interaction guards ---------------------------- */

  // Typed value / Enter / blur on a digital quantity input.
  // Runs in capture phase, BEFORE the theme's x-on:change handler reads the
  // value, so the AJAX update posts the corrected quantity.
  document.addEventListener(
    'change',
    function (e) {
      var el = e.target;
      if (!el || !el.matches || !el.matches('input[data-digital-limit]')) return;
      var max = getMax(el);
      var val = parseInt(el.value, 10);
      if (!isNaN(val) && val > max) {
        el.value = max;
        warn();
      }
    },
    true
  );

  // Plus button on a digital line: block the increment once at the limit.
  document.addEventListener(
    'click',
    function (e) {
      var btn = e.target.closest
        ? e.target.closest('button[data-digital-limit]')
        : null;
      if (!btn) return;
      var input = inputForButton(btn);
      var max = getMax(btn);
      var val = input ? parseInt(input.value, 10) : 0;
      if (!isNaN(val) && val >= max) {
        e.preventDefault();
        e.stopImmediatePropagation(); // stop the theme's plusItemQty handler
        warn();
      }
    },
    true
  );

  /* --------------------- auto-correct already-over lines -------------------- */

  function enforceExisting() {
    var inputs = document.querySelectorAll('input[data-digital-limit]');
    var corrected = false;
    for (var i = 0; i < inputs.length; i++) {
      var el = inputs[i];
      var max = getMax(el);
      var val = parseInt(el.value, 10);
      if (!isNaN(val) && val > max) {
        el.value = max;
        corrected = true;
        // Reuse the theme's own update pipeline (bubble-phase x-on:change).
        el.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
    if (corrected) warn();
  }

  function debounce(fn, wait) {
    var t;
    return function () {
      clearTimeout(t);
      t = setTimeout(fn, wait);
    };
  }

  function init() {
    enforceExisting();
    // Cart sections are replaced on every AJAX update; re-check after each.
    var obs = new MutationObserver(debounce(enforceExisting, 300));
    obs.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
